import { useCallback, useMemo } from 'react';
import type {
  AppointmentResponse,
  AppointmentTimingInfo,
  NextAppointmentResponse,
} from '@features/appointments';
import { useGetNextAppointmentQuery } from '@features/appointments';
import { selectActiveWorkflowEncounterId } from '@features/encounters';
import { useAppSelector } from '@store/hooks';

interface AppointmentRecommendation {
  type: 'current' | 'current_overtime' | 'upcoming';
  appointment: AppointmentResponse;
  confidence: 'high' | 'medium';
  message: string;
}

// Add constants for time thresholds
const APPOINTMENT_GRACE_PERIOD = 10; // Minutes before/after appointment start to consider "on time"
const OVERTIME_THRESHOLD = 10; // Minutes after scheduled end to still associate with appointment

/**
 * Helper function to check if appointment is within time window.
 * Uses a hybrid approach:
 * 1. Trusts backend's grace period flag if set (may include business rules we don't know)
 * 2. Falls back to our time calculations for consistency
 */
const isWithinTimeWindow = (
  timing: AppointmentTimingInfo | undefined,
  minutesBefore: number = APPOINTMENT_GRACE_PERIOD,
  minutesAfter: number = APPOINTMENT_GRACE_PERIOD,
): boolean => {
  if (!timing) return false;

  // Trust backend's grace period determination if available
  if (timing.is_within_grace_period) return true;

  // Fallback to our calculations for consistency
  // This ensures we handle cases where backend flag might be null/undefined
  const minutes = timing.minutes_until_start;
  return (
    minutes !== undefined &&
    minutes !== null &&
    minutes >= -minutesAfter &&
    minutes <= minutesBefore
  );
};

/**
 * Helper function to check if an appointment is eligible for collision detection.
 * Only appointments with no encounter or pending encounter status should be considered.
 * Appointments with 'processing' or 'completed' status are already being handled.
 */
const isAppointmentEligibleForCollision = (
  appointment: AppointmentResponse | undefined,
): boolean => {
  if (!appointment) return false;

  // If no encounter exists, the appointment is eligible
  if (!appointment.encounter) return true;

  // Handle both string and object encounter types
  if (typeof appointment.encounter === 'string') {
    // If encounter is just an ID string, we can't check status, assume eligible
    return true;
  }

  // Check the encounter status - only 'pending' or undefined are eligible
  const encounterObj = appointment.encounter as { status?: string };
  return !encounterObj.status || encounterObj.status === 'pending';
};

/**
 * Filter an array of appointment contexts to only include eligible appointments.
 * Applies both status filtering and time window filtering.
 */
const filterEligibleAppointments = <
  T extends { appointment: AppointmentResponse; timing: AppointmentTimingInfo },
>(
  appointments: T[] | null | undefined,
  checkTimeWindow: boolean = false,
): T[] => {
  if (!appointments || appointments.length === 0) return [];

  return appointments.filter(ctx => {
    // Check eligibility by encounter status
    if (!isAppointmentEligibleForCollision(ctx.appointment)) return false;

    // Optionally check time window
    if (checkTimeWindow) {
      return isWithinTimeWindow(ctx.timing, APPOINTMENT_GRACE_PERIOD, APPOINTMENT_GRACE_PERIOD);
    }

    return true;
  });
};

/**
 * Get all eligible current appointments (filtering by status and excluding overtime).
 */
const getEligibleCurrentAppointments = (
  appointmentData: NextAppointmentResponse | undefined,
): Array<{ appointment: AppointmentResponse; timing: AppointmentTimingInfo }> => {
  const current = appointmentData?.current;
  if (!current) return [];

  // Filter by eligibility and exclude overtime appointments
  return filterEligibleAppointments(current).filter(ctx => !ctx.timing.is_overtime);
};

/**
 * Get all eligible overtime appointments (filtering by status and within threshold).
 */
const getEligibleOvertimeAppointments = (
  appointmentData: NextAppointmentResponse | undefined,
): Array<{
  appointment: AppointmentResponse;
  timing: AppointmentTimingInfo;
  overtimeMinutes: number;
}> => {
  const current = appointmentData?.current;
  if (!current) return [];

  // Filter by eligibility and include only overtime appointments within threshold
  return filterEligibleAppointments(current)
    .filter(ctx => ctx.timing.is_overtime)
    .map(ctx => {
      const overtimeMinutes = (ctx.timing.minutes_elapsed || 0) - (ctx.appointment.duration || 0);
      return { ...ctx, overtimeMinutes };
    })
    .filter(ctx => ctx.overtimeMinutes <= OVERTIME_THRESHOLD);
};

/**
 * Get all eligible upcoming appointments (filtering by status and time window).
 */
const getEligibleNextAppointments = (
  appointmentData: NextAppointmentResponse | undefined,
): Array<{ appointment: AppointmentResponse; timing: AppointmentTimingInfo }> => {
  const next = appointmentData?.next;
  if (!next) return [];

  // Filter by eligibility and time window
  return filterEligibleAppointments(next, true);
};

/**
 * Hook that detects potential appointment conflicts when starting a recording.
 * Uses the context-aware appointments API to determine if there's a current
 * or upcoming appointment that should be associated with the recording.
 */
export const useAppointmentCollision = () => {
  const activeEncounterId = useAppSelector(selectActiveWorkflowEncounterId);
  const { data, isLoading, refetch } = useGetNextAppointmentQuery(
    {
      contextMode: true,
      includeRecentMinutes: 10,
      includeCurrent: true,
      num: 3, // Get a few upcoming for preview
    },
    {
      refetchOnFocus: true,
    },
  );

  // Analyze timing to determine best appointment match
  const getRecommendation = useCallback((
    appointmentData: NextAppointmentResponse | undefined,
  ): AppointmentRecommendation | null => {
    if (!appointmentData) return null;

    // Get all eligible appointments using new array helpers
    const eligibleCurrent = getEligibleCurrentAppointments(appointmentData);
    const eligibleOvertime = getEligibleOvertimeAppointments(appointmentData);
    const eligibleNext = getEligibleNextAppointments(appointmentData);

    // Calculate total eligible appointments count
    const totalEligible = eligibleCurrent.length + eligibleOvertime.length + eligibleNext.length;

    // If no eligible appointments, no collision
    if (totalEligible === 0) return null;

    // Case 1: Multiple eligible appointments - always show modal
    if (totalEligible > 1) {
      // Pick the first current, overtime, or next appointment as the "recommended" one
      const firstAppointment = eligibleCurrent[0] || eligibleOvertime[0] || eligibleNext[0];

      return {
        type:
          eligibleCurrent.length > 0
            ? 'current'
            : eligibleOvertime.length > 0
              ? 'current_overtime'
              : 'upcoming',
        appointment: firstAppointment.appointment,
        confidence: 'medium',
        message: `You have ${totalEligible} appointments. Which would you like to record?`,
      };
    }

    // Case 2: Single eligible current appointment (not in overtime)
    if (eligibleCurrent.length === 1) {
      const { appointment, timing } = eligibleCurrent[0];

      // HIGH CONFIDENCE: If this appointment's encounter IS the active encounter
      // Auto-navigate back to it - user is resuming their active work
      // This is safe because:
      // 1. activeEncounterId is set when user navigates to encounter
      // 2. activeEncounterId is cleared when user submits or deletes encounter
      // 3. If user "saves for later" and navigates away, activeEncounterId is cleared
      // 4. So if activeEncounterId matches, user is ACTIVELY working on this encounter
      if (activeEncounterId && appointment.encounter && typeof appointment.encounter === 'object') {
        const encounter = appointment.encounter as { encounter_id?: string };
        if (encounter.encounter_id === activeEncounterId) {
          return {
            type: 'current',
            appointment,
            confidence: 'high',
            message: `Resuming ${appointment.patient?.name || 'current patient'} (${
              timing.minutes_remaining || 0
            } min remaining)`,
          };
        }
      }

      // MEDIUM CONFIDENCE: All other single appointment cases
      // Show modal to give user choice (prevents "save for later" forced resume bug)
      //
      // This prevents the edge case where:
      // 1. User starts recording at appointment time
      // 2. User does a quick 5-minute encounter and clicks "Save for Later"
      // 3. User clicks "Start Recording" again immediately (still within grace period)
      // 4. Without modal: Would auto-navigate back to saved encounter (user forced back)
      // 5. With modal: User can choose to resume OR start new recording
      //
      // Since activeEncounterId is cleared after "Save for Later", this will NOT
      // match the high-confidence check above, so modal will be shown.
      return {
        type: 'current',
        appointment,
        confidence: 'medium',
        message: `Record for ${appointment.patient?.name || 'current patient'}?`,
      };
    }

    // Case 3: Single eligible overtime appointment
    if (eligibleOvertime.length === 1) {
      const { appointment } = eligibleOvertime[0];

      return {
        type: 'current_overtime',
        appointment,
        confidence: 'medium',
        message: `Record for ${appointment.patient?.name || 'current patient'}?`,
      };
    }

    // Case 4: Single eligible upcoming appointment
    if (eligibleNext.length === 1) {
      const { appointment, timing } = eligibleNext[0];
      const minutesUntil = timing.minutes_until_start || 0;
      const timeDescription =
        minutesUntil < 0
          ? 'now'
          : `starts in ${minutesUntil} min`;

      return {
        type: 'upcoming',
        appointment,
        confidence: 'medium',
        message: `Record for ${appointment.patient?.name || 'upcoming patient'}? (${timeDescription})`,
      };
    }

    return null;
  }, [activeEncounterId]);

  const recommendation = useMemo(() => getRecommendation(data), [data, getRecommendation]);

  // Memoize eligible appointment arrays for better performance
  const eligibleCurrentAppointments = useMemo(() => getEligibleCurrentAppointments(data), [data]);
  const eligibleOvertimeAppointments = useMemo(() => getEligibleOvertimeAppointments(data), [data]);
  const eligibleNextAppointments = useMemo(() => getEligibleNextAppointments(data), [data]);

  // Combine all eligible appointments into a single array
  const allEligibleAppointments = useMemo(() => {
    return [
      ...eligibleCurrentAppointments.map(ctx => ctx.appointment),
      ...eligibleOvertimeAppointments.map(ctx => ctx.appointment),
      ...eligibleNextAppointments.map(ctx => ctx.appointment),
    ];
  }, [eligibleCurrentAppointments, eligibleOvertimeAppointments, eligibleNextAppointments]);

  const hasCollision = useMemo(() => {
    return allEligibleAppointments.length > 0;
  }, [allEligibleAppointments.length]);

  return {
    appointmentData: data,
    eligibleCurrentAppointments,
    eligibleOvertimeAppointments,
    eligibleNextAppointments,
    allEligibleAppointments,
    isLoading,
    refetch,
    recommendation,
    hasCollision,
  };
};
